import { ReactElement, useEffect, useState } from "react"
import { Blockquote, Button, FormLabel, Loading, TextInput } from "@wfp/ui"
import Select, { SingleValue } from "react-select"
import moment from "moment"
import { HH_MODULE, formatDateTime } from "constants/constants"
import { BaseXlsHH } from "interfaces/xlsForm"
import { CommonGetOptions } from "interfaces/dataInterfaces"
import { useGetAllCategoriesQuery } from "services/household/categories"
import { useGetSubCategoriesQuery } from "services/household/subCategories"
import { useUpdateBaseXlsHHMutation } from "services/household/xlsFormSchemas"
import useCommon from "hooks/useCommon"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import RenderRow from "components/Shared/Property/RenderRow"

type Props = {
  data: BaseXlsHH
  mode: string
  changeMode: () => void
}

const BaseXlsFormDetails = ({ data, mode, changeMode }: Props): ReactElement => {
  const { showSnackBar } = useCommon()
  const [values, setValues] = useState<BaseXlsHH>(data)
  const isUpdated = JSON.stringify(data) === JSON.stringify(values)

  const { data: categories, isFetching: loadingCategories } = useGetAllCategoriesQuery()
  const { data: subCategories, isFetching: loadingSubCategories } = useGetSubCategoriesQuery(
    { categoryId: Number(values?.categoryId) },
    { skip: !values?.categoryId },
  )

  const [updateBaseXlsHH, { isLoading: isUpdating }] = useUpdateBaseXlsHHMutation()

  const reset = (): void => {
    setValues(data)
    changeMode()
  }

  const handleCategories = (item: SingleValue<CommonGetOptions>): void => {
    setValues(prev => ({ ...prev, categoryId: Number(item?.value), subCategoryId: 0 }))
  }

  const handleSubCategories = (item: SingleValue<CommonGetOptions>): void => {
    setValues(prev => ({ ...prev, subCategoryId: Number(item?.value) }))
  }

  const handleEdit = async (): Promise<void> => {
    if (!values?.xlsFormName || !values?.categoryId || !values?.subCategoryId) {
      showSnackBar("Please fill all the required fields", "warning")
      return
    }
    try {
      await updateBaseXlsHH(values).unwrap()
      showSnackBar("Base Xls form edited successfully")
      changeMode()
    } catch (error) {
      const errorMessage =
        (error as { data: { message: string } }).data?.message ?? "Something went wrong, please try again"
      showSnackBar(errorMessage, "error")
    }
  }

  useEffect(() => {
    setValues(data)
  }, [data])

  return (
    <div className="p-3">
      {loadingCategories || loadingSubCategories ? (
        <Loading small />
      ) : (
        mode === "view" && (
          <div>
            {!data?.isValid && (
              <Blockquote kind="error" withIcon className="mb-2">
                <p className="py-1">The XLSForm is invalid.</p>
              </Blockquote>
            )}

            <table>
              <RenderRow label="Name" value={data?.xlsFormName} />
              <RenderRow
                label="Category"
                value={categories?.find(cat => Number(cat?.value) === values?.categoryId)?.name ?? ""}
              />
              <RenderRow
                label="Type"
                value={subCategories?.find(subCat => Number(subCat?.value) === values?.subCategoryId)?.name ?? ""}
              />
              <RenderRow label="Created by" value={data?.userName} />
              <RenderRow
                label="Created date"
                value={data?.xlsFormCreateDate ? moment(data.xlsFormCreateDate).format(formatDateTime) : ""}
              />
              <RenderRow
                label="Updated date"
                value={data?.xlsFormUpdateDate ? moment(data.xlsFormUpdateDate).format(formatDateTime) : ""}
              />
              <RenderRow label="Archived" value={data?.xlsFormsIsArchived ? "Yes" : "No"} />
            </table>
          </div>
        )
      )}

      {mode === "edit" && !!values?.coForms.length && (
        <div>
          <Blockquote kind="warning" withIcon>
            <p className="py-1">The base xls form is associated with one or more xls forms.</p>
          </Blockquote>

          <div className="mt-2 mb-5">
            <p className="text-justify">
              <b>Associated xls forms</b>: <span className="italic">{values?.coForms?.join(", ")}</span>
            </p>
          </div>
        </div>
      )}

      {mode === "edit" && (
        <div className="space-y-5">
          <TextInput
            labelText="Name"
            name="name"
            placeholder="Name of the xls form schema"
            type="text"
            onChange={e => setValues(prev => ({ ...prev, xlsFormName: e.target.value }))}
            value={values?.xlsFormName}
            className="border-gray-300 rounded-md text-base w-50"
          />
          <div className="w-50">
            <FormLabel htmlFor="category">Category</FormLabel>
            <Select
              inputId="category"
              isLoading={loadingCategories}
              placeholder="Select category"
              options={categories ?? []}
              getOptionLabel={option => option.name}
              getOptionValue={option => option.value}
              value={categories?.find(cat => Number(cat?.value) === values?.categoryId) ?? null}
              onChange={item => handleCategories(item)}
            />
          </div>
          <div className="w-50">
            <FormLabel htmlFor="sub-category">Type</FormLabel>
            <Select
              inputId="sub-category"
              isLoading={loadingSubCategories}
              placeholder="Select type"
              options={subCategories ?? []}
              getOptionLabel={option => option.name}
              getOptionValue={option => option.value}
              value={subCategories?.find(subCat => Number(subCat?.value) === values?.subCategoryId) ?? null}
              onChange={item => handleSubCategories(item)}
            />
          </div>
        </div>
      )}

      <PermissionWrapper moduleId={HH_MODULE} subModule="baseXLSForms" feature="edit">
        <div className="flex justify-between mt-4">
          <Button onClick={reset} kind={mode === "edit" ? "tertiary" : "primary"} disabled={isUpdating}>
            {mode === "edit" ? "Cancel" : "Edit"}
          </Button>

          {mode === "edit" && (
            <Button onClick={handleEdit} disabled={isUpdating || isUpdated}>
              Save
            </Button>
          )}
        </div>
      </PermissionWrapper>
    </div>
  )
}

export default BaseXlsFormDetails
